<script setup lang="ts">
/**
 * AbTestModal — 1:1 порт docs/mockups/modal-ab-test.html (inbox-181).
 *
 * Variant rows (URL + weight % slider), sum=100, win-criterion (CR/clicks),
 * duration_days. POST /api/v1/links/{id}/ab-tests — UiDevCard fallback.
 *
 * Open: modals.open('ab-test', { linkId: 7 }).
 */

import { computed, ref, watch } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiField from '@/components/ui/UiField.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

interface Variant {
  url: string;
  weight: number;
}

const variants = ref<Variant[]>([
  { url: '', weight: 50 },
  { url: '', weight: 50 },
]);
const metric = ref<'click_rate' | 'conversion' | 'time_on_page'>('click_rate');
const durationDays = ref(14);
const submitting = ref(false);

watch(() => modals.activeKey, (k) => {
  if (k !== 'ab-test') return;
  variants.value = [{ url: '', weight: 50 }, { url: '', weight: 50 }];
  metric.value = 'click_rate';
  durationDays.value = 14;
  submitting.value = false;
});

const totalWeight = computed(() => variants.value.reduce((s, v) => s + v.weight, 0));
const isValid = computed(() => totalWeight.value === 100 && variants.value.every((v) => v.url.trim().length > 0));

function addVariant() {
  if (variants.value.length >= 5) return;
  variants.value.push({ url: '', weight: 0 });
}

function removeVariant(i: number) {
  if (variants.value.length <= 2) return;
  variants.value.splice(i, 1);
}

function rebalance() {
  const n = variants.value.length;
  const w = Math.floor(100 / n);
  variants.value.forEach((v, i) => { v.weight = i === n - 1 ? 100 - w * (n - 1) : w; });
}

async function submit() {
  if (!isValid.value) return;
  submitting.value = true;
  await new Promise((r) => setTimeout(r, 600));
  submitting.value = false;
  modals.emit('ab-test', { variants: variants.value, metric: metric.value, duration_days: durationDays.value });
  modals.close();
}

function close() {
  if (!modals.pending) modals.close();
}
</script>

<template>
  <UiModal
    title="Создать A/B-тест"
    subtitle="Распределите трафик между вариантами и выберите метрику победы."
    size="lg"
    :disable-close="submitting"
    @close="close"
  >
    <form id="abtest-form" @submit.prevent="submit">
      <UiField label="Варианты">
        <div v-for="(v, i) in variants" :key="i" class="variant-row">
          <span class="variant-letter">{{ String.fromCharCode(65 + i) }}</span>
          <UiInput v-model="v.url" type="url" :placeholder="`https://example.com/variant-${String.fromCharCode(65 + i).toLowerCase()}`" :disabled="submitting" />
          <input v-model.number="v.weight" type="number" min="0" max="100" class="weight-input" :disabled="submitting" />
          <span class="pct">%</span>
          <button type="button" class="remove-btn" :disabled="variants.length <= 2 || submitting" @click="removeVariant(i)" title="Удалить вариант">×</button>
        </div>
        <div class="variant-tools">
          <button type="button" class="link-btn" :disabled="variants.length >= 5 || submitting" @click="addVariant">+ Добавить вариант</button>
          <button type="button" class="link-btn" :disabled="submitting" @click="rebalance">Распределить равномерно</button>
        </div>
        <p :class="['weight-status', { ok: totalWeight === 100, err: totalWeight !== 100 }]">
          Сумма: <strong>{{ totalWeight }}%</strong> {{ totalWeight === 100 ? '✓' : '— должна быть 100' }}
        </p>
      </UiField>

      <UiField label="Метрика победы">
        <select v-model="metric" class="select" :disabled="submitting">
          <option value="click_rate">CTR (click-through rate)</option>
          <option value="conversion">Конверсия в событие</option>
          <option value="time_on_page">Время на странице</option>
        </select>
      </UiField>

      <UiField label="Длительность теста" hint="3-30 дней. После окончания — авто-promote победителя.">
        <input v-model.number="durationDays" type="number" min="3" max="30" class="duration-input" :disabled="submitting" />
        <span class="hint-inline">дней</span>
      </UiField>

      <UiDevCard
        feature="POST /api/v1/links/{id}/ab-tests"
        message="Backend A/B-engine с traffic-splitter pending — submit stub'нут. Создание test'a + redirect-роутер на backend stage в backlog."
      />
    </form>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="close">Отмена</UiButton>
      <UiButton type="submit" form="abtest-form" :loading="submitting" :disabled="!isValid">Запустить тест</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.variant-row { display: grid; grid-template-columns: 28px 1fr 70px auto 28px; gap: 8px; align-items: center; margin-bottom: 8px; }
.variant-letter { width: 28px; height: 28px; background: var(--accent-soft); color: var(--accent-hi); border-radius: 6px; display: grid; place-items: center; font-weight: 700; font-size: 13px; }
.weight-input { width: 64px; padding: 7px 8px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 6px; color: var(--fg); font: inherit; font-size: 13.5px; text-align: right; }
.pct { color: var(--fg-muted); font-size: 13px; }
.remove-btn { width: 26px; height: 26px; background: transparent; border: 0; color: var(--fg-dim); cursor: pointer; font-size: 18px; border-radius: 5px; }
.remove-btn:hover:not(:disabled) { color: var(--danger); background: var(--danger-soft); }
.remove-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.variant-tools { display: flex; gap: 16px; margin-top: 8px; }
.link-btn { background: transparent; border: 0; color: var(--accent-hi); font-size: 12.5px; cursor: pointer; font-family: inherit; }
.link-btn:hover:not(:disabled) { text-decoration: underline; }
.weight-status { font-size: 12.5px; margin: 8px 0 0; }
.weight-status.ok { color: var(--success); }
.weight-status.err { color: var(--warning); }
.select { width: 100%; padding: 8px 12px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 7px; color: var(--fg); font: inherit; font-size: 13.5px; }
.duration-input { width: 80px; padding: 7px 8px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 6px; color: var(--fg); font: inherit; font-size: 13.5px; }
.hint-inline { margin-left: 6px; color: var(--fg-muted); font-size: 13px; }
</style>
